// Document indexes' server state (/api/me/doc-indexes): every query key and
// mutation of the indexes lives here.
//
// Promise:
// - Keys start with ["me", "documents", "indexes", ...], so what changes the
//   documents (a rename, an archive) can refresh the trees with one prefix.
// - Putting in, moving and taking out change the cached tree at once and roll
//   back when the server refuses; the tree is fetched again when it settles.
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  DocIndexDTO,
  DocIndexNodeDTO,
  DocIndexRefDTO,
  DocIndexTreeDTO,
} from "@trameria/core/doc-indexes-shared";
import { useApi } from "../../app/context";
import { type Api, idempotencyKeyFor } from "../../data/api";
import { documentsPrefix, listPrefix } from "../queries";

export const indexesPrefix = [...documentsPrefix, "indexes"] as const;

export const indexListKey = [...indexesPrefix, "list"] as const;

/** The owner's indexes, the latest change first. */
export const indexListQuery = (api: Api) =>
  queryOptions({
    queryKey: indexListKey,
    queryFn: async () =>
      (await api<{ indexes: DocIndexDTO[] }>("/api/me/doc-indexes")).indexes,
  });

/**
 * The indexes a document may be put in: one's own for a personal document,
 * those of its workspace for a shared one.
 */
export const indexesForQuery = (api: Api, pageId: string) =>
  queryOptions({
    queryKey: [...indexListKey, "for", pageId] as const,
    queryFn: async () =>
      (
        await api<{ indexes: DocIndexDTO[] }>(
          `/api/me/doc-indexes?for=${encodeURIComponent(pageId)}`,
        )
      ).indexes,
  });

export const treePrefix = [...indexesPrefix, "tree"] as const;
export const treeKey = (key: string) => [...treePrefix, key] as const;

/** One index and its tree, by the key the URL names. */
export const treeQuery = (api: Api, key: string) =>
  queryOptions({
    queryKey: treeKey(key),
    queryFn: () =>
      api<DocIndexTreeDTO>(`/api/me/doc-indexes/${encodeURIComponent(key)}`),
  });

export const containingPrefix = [...indexesPrefix, "containing"] as const;
export const containingKey = (pageId: string) =>
  [...containingPrefix, pageId] as const;

/** The indexes a document is in. */
export const containingQuery = (api: Api, pageId: string) =>
  queryOptions({
    queryKey: containingKey(pageId),
    queryFn: async () =>
      (
        await api<{ indexes: DocIndexRefDTO[] }>(
          `/api/me/pages/${pageId}/doc-indexes`,
        )
      ).indexes,
  });

/** Where a document goes: under `parentId` (null: the top), after `afterId` (null: first; absent: last). */
export interface PlaceInput {
  index: DocIndexDTO;
  pageId: string;
  parentId: string | null;
  afterId?: string | null;
  /** For the tree shown at once: the document's row. */
  row?: Omit<DocIndexNodeDTO, "children">;
}

// ---- the tree as data ---------------------------------------------------------

/** The tree without `pageId`; what was under it is returned too. */
function take(
  nodes: readonly DocIndexNodeDTO[],
  pageId: string,
): { nodes: DocIndexNodeDTO[]; taken: DocIndexNodeDTO | null } {
  let taken: DocIndexNodeDTO | null = null;
  const walk = (list: readonly DocIndexNodeDTO[]): DocIndexNodeDTO[] =>
    list.flatMap((n) => {
      if (n.pageId === pageId) {
        taken = n;
        return [];
      }
      return [{ ...n, children: walk(n.children) }];
    });
  return { nodes: walk(nodes), taken };
}

/** The tree with `node` put under `parentId` after `afterId`. */
function put(
  nodes: readonly DocIndexNodeDTO[],
  node: DocIndexNodeDTO,
  parentId: string | null,
  afterId: string | null | undefined,
): DocIndexNodeDTO[] {
  const into = (list: readonly DocIndexNodeDTO[]): DocIndexNodeDTO[] => {
    const out = [...list];
    const after = afterId ? out.findIndex((n) => n.pageId === afterId) : -1;
    const at = afterId === null ? 0 : after < 0 ? out.length : after + 1;
    out.splice(at, 0, node);
    return out;
  };
  if (parentId === null) return into(nodes);
  return nodes.map((n) =>
    n.pageId === parentId
      ? { ...n, children: into(n.children) }
      : { ...n, children: put(n.children, node, parentId, afterId) },
  );
}

/** The tree with `pageId` taken out and what was under it lifted to its place. */
export function lifted(
  nodes: readonly DocIndexNodeDTO[],
  pageId: string,
): DocIndexNodeDTO[] {
  return nodes.flatMap((n) =>
    n.pageId === pageId
      ? n.children
      : [{ ...n, children: lifted(n.children, pageId) }],
  );
}

/** The tree with a document put (or moved, with what is under it) to its place. */
export function placed(
  nodes: readonly DocIndexNodeDTO[],
  input: Omit<PlaceInput, "index">,
): DocIndexNodeDTO[] {
  const { nodes: rest, taken } = take(nodes, input.pageId);
  const node = taken ?? (input.row ? { ...input.row, children: [] } : null);
  if (!node) return [...nodes];
  return put(rest, node, input.parentId, input.afterId);
}

/** Every document of the tree, top to bottom. */
export function flat(nodes: readonly DocIndexNodeDTO[]): DocIndexNodeDTO[] {
  return nodes.flatMap((n) => [n, ...flat(n.children)]);
}

/** The parent of `pageId` (null: the top) and its siblings, or null when it is not in the tree. */
export function siblingsOf(
  nodes: readonly DocIndexNodeDTO[],
  pageId: string,
  parent: DocIndexNodeDTO | null = null,
): { parent: DocIndexNodeDTO | null; list: readonly DocIndexNodeDTO[] } | null {
  if (nodes.some((n) => n.pageId === pageId)) return { parent, list: nodes };
  for (const n of nodes) {
    const found = siblingsOf(n.children, pageId, n);
    if (found) return found;
  }
  return null;
}

// ---- mutations -------------------------------------------------------------------

function useTreeChange<I extends { index: DocIndexDTO }>(
  send: (api: Api, input: I) => Promise<unknown>,
  change: (nodes: readonly DocIndexNodeDTO[], input: I) => DocIndexNodeDTO[],
) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: I) => send(api, input),
    onMutate: async (input) => {
      const queryKey = treeKey(input.index.key);
      await qc.cancelQueries({ queryKey });
      const before = qc.getQueryData<DocIndexTreeDTO>(queryKey);
      if (before)
        qc.setQueryData<DocIndexTreeDTO>(queryKey, {
          ...before,
          nodes: change(before.nodes, input),
        });
      return { undo: () => qc.setQueryData(queryKey, before) };
    },
    onError: (_e, _i, context) => context?.undo(),
    onSettled: () => void qc.invalidateQueries({ queryKey: indexesPrefix }),
  });
}

/** Puts a document in an index, or moves it in it. */
export function usePlaceInIndex() {
  return useTreeChange<PlaceInput>(
    (api, input) =>
      api(`/api/me/doc-indexes/${input.index.id}/entries`, {
        method: "POST",
        body: {
          pageId: input.pageId,
          parentId: input.parentId,
          ...(input.afterId !== undefined ? { afterId: input.afterId } : {}),
        },
      }),
    (nodes, input) => placed(nodes, input),
  );
}

/** Takes a document out of an index (what was under it takes its place). */
export function useDetachFromIndex() {
  return useTreeChange<{ index: DocIndexDTO; pageId: string }>(
    (api, input) =>
      api(`/api/me/doc-indexes/${input.index.id}/entries/${input.pageId}`, {
        method: "DELETE",
      }),
    (nodes, input) => lifted(nodes, input.pageId),
  );
}

/**
 * Makes an index (with the documents given at its top); `beside` a document,
 * in the same place as it (a shared document's index is its workspace's).
 */
export function useCreateIndex() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      pageIds?: string[];
      beside?: string;
    }) =>
      api<DocIndexDTO>("/api/me/doc-indexes", {
        method: "POST",
        body: {
          title: input.title,
          ...(input.beside !== undefined ? { beside: input.beside } : {}),
          ...(input.pageIds?.length
            ? {
                tree: input.pageIds.map((pageId) => ({
                  pageId,
                  parentId: null,
                })),
              }
            : {}),
        },
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: indexesPrefix });
      void qc.invalidateQueries({ queryKey: listPrefix });
    },
  });
}
